// Global Instructions Rule Applied!
//
// Configuration → Stage Actions: configure actions that fire when a deal
// enters a pipeline stage (v1: send a templated email from the reservoir@
// mailbox to a list of internal users). Grid of configured actions + a
// create/edit modal. Reads via the config endpoints (view__ RPCs), writes via
// the allow-listed cfg batch endpoints — no global-configs involvement.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type {
  StageActionAdminModel,
  StageActionTypeModel
} from '../../../state_manager/data_models/stage_action_model.ts'
import {
  fetchStageActions,
  fetchStageActionTypes,
  createStageAction,
  updateStageAction,
  deleteStageAction,
  type StageActionSavePayload
} from '../services/stageActionsConfigService.ts'
import { fetchPipelines, fetchPipelineStages } from '../services/pipelinesConfigService.ts'
import { fetchEmailDesignTemplatesFiltered } from '../../email/services/emailDesignTemplateService.ts'
import { searchSystemUsers } from '../../email/services/emailService.ts'

const { Title, Text } = Typography

interface Option {
  value: string
  label: string
}

interface EditorState {
  open: boolean
  /** null = create */
  editing: StageActionAdminModel | null
  pipelineId: string | null
  pipelineConfigId: string | null
  actionTypeId: string | null
  templateId: string | null
  /** To header: fixed users + dynamic deal-team roles */
  recipients: Option[]
  teamRoles: string[]
  /** Cc header: same shapes */
  ccRecipients: Option[]
  ccTeamRoles: string[]
  enabled: boolean
}

const CLOSED_EDITOR: EditorState = {
  open: false,
  editing: null,
  pipelineId: null,
  pipelineConfigId: null,
  actionTypeId: null,
  templateId: null,
  recipients: [],
  teamRoles: [],
  ccRecipients: [],
  ccTeamRoles: [],
  enabled: true
}

// Deal-team roles a send-email action can address dynamically. Labels mirror
// the deal form's team fields; values mirror cfg.stage_action_recipients
// team_role and the stage_action__deal_team_emails resolver.
const TEAM_ROLE_OPTIONS: Option[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'sourced_by', label: 'Sourced by' },
  { value: 'introduced_by', label: 'Introduced by' }
]
const TEAM_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  TEAM_ROLE_OPTIONS.map((o) => [o.value, o.label])
)

// Editor hydration helpers — split an admin row's recipient list by header
const userOptionsOf = (row: StageActionAdminModel, mode: string): Option[] =>
  row.recipients
    .filter(
      (r) =>
        r.stage_action_recipients__recipient_kind === 'user' &&
        r.stage_action_recipients__address_mode === mode &&
        r.system_users__id
    )
    .map((r) => ({
      value: r.system_users__id as string,
      label: r.system_users__name || r.system_users__email || (r.system_users__id as string)
    }))

const teamRolesOf = (row: StageActionAdminModel, mode: string): string[] =>
  row.recipients
    .filter(
      (r) =>
        r.stage_action_recipients__recipient_kind === 'team_role' &&
        r.stage_action_recipients__address_mode === mode
    )
    .map((r) => r.stage_action_recipients__team_role)
    .filter((role): role is string => Boolean(role))

export default function StageActionsPanel() {
  const token = () => getAccessToken(store.getState().auth)

  const [rows, setRows] = useState<StageActionAdminModel[]>([])
  const [types, setTypes] = useState<StageActionTypeModel[]>([])
  const [pipelines, setPipelines] = useState<Option[]>([])
  const [templates, setTemplates] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const [editor, setEditor] = useState<EditorState>(CLOSED_EDITOR)
  const [stageOptions, setStageOptions] = useState<Option[]>([])
  const [stagesLoading, setStagesLoading] = useState(false)
  const [userOptions, setUserOptions] = useState<Option[]>([])
  const [userSearching, setUserSearching] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchStageActions(token())
      if (result.error) {
        showErrorToast(result.error)
        return
      }
      setRows(result.data)
    } finally {
      setLoading(false)
    }
  }, [])

  // Static option universes: registry, pipelines, templates.
  useEffect(() => {
    reload()
    fetchStageActionTypes(token()).then((r) => {
      if (r.error) showErrorToast(r.error)
      setTypes(r.data)
    })
    fetchPipelines([], [], 1, 200, token()).then((r) => {
      if (r.error) showErrorToast(r.error)
      setPipelines(
        r.data
          .filter((p) => p.pipelines__name)
          .map((p) => ({ value: p.pipelines__id, label: String(p.pipelines__name) }))
      )
    })
    fetchEmailDesignTemplatesFiltered([{ key: 'email_design_templates__name', direction: 'asc' }], [], 1, 200, token()).then(
      (r) => {
        if (r.error) showErrorToast(r.error)
        setTemplates(
          (r.data || []).map((t) => ({
            value: t.email_design_templates__id,
            label: t.email_design_templates__name
          }))
        )
      }
    )
  }, [reload])

  const selectableTypes = useMemo(
    () => types.filter((t) => t.stage_action_types__selectable),
    [types]
  )

  // Editor helpers -----------------------------------------------------------

  const loadStages = useCallback(async (pipelineId: string) => {
    setStagesLoading(true)
    try {
      const result = await fetchPipelineStages(pipelineId, token())
      if (result.error) showErrorToast(result.error)
      setStageOptions(
        result.data
          .filter((s) => s.pipeline_stages__name)
          .map((s) => ({ value: s.pipeline_config__id, label: String(s.pipeline_stages__name) }))
      )
    } finally {
      setStagesLoading(false)
    }
  }, [])

  const openCreate = useCallback(() => {
    setStageOptions([])
    setUserOptions([])
    setEditor({
      ...CLOSED_EDITOR,
      open: true,
      actionTypeId:
        selectableTypes.length === 1 ? selectableTypes[0].stage_action_types__id : null
    })
  }, [selectableTypes])

  const openEdit = useCallback(
    (row: StageActionAdminModel) => {
      setUserOptions([])
      setEditor({
        open: true,
        editing: row,
        pipelineId: row.pipelines__id,
        pipelineConfigId: row.stage_actions__pipeline_config_id,
        actionTypeId: row.stage_action_types__id,
        templateId: row.email_design_templates__id,
        recipients: userOptionsOf(row, 'to'),
        teamRoles: teamRolesOf(row, 'to'),
        ccRecipients: userOptionsOf(row, 'cc'),
        ccTeamRoles: teamRolesOf(row, 'cc'),
        enabled: row.stage_actions__enabled
      })
      loadStages(row.pipelines__id)
    },
    [loadStages]
  )

  const onUserSearch = useCallback(async (term: string) => {
    if (!term || term.trim().length < 2) return
    setUserSearching(true)
    try {
      const result = await searchSystemUsers(term.trim(), token())
      if (result.error) showErrorToast(result.error)
      setUserOptions(result.data.map((u) => ({ value: u.value, label: u.label })))
    } finally {
      setUserSearching(false)
    }
  }, [])

  const submit = useCallback(async () => {
    if (!editor.pipelineConfigId) {
      showErrorToast('Select a pipeline stage')
      return
    }
    if (!editor.actionTypeId) {
      showErrorToast('Select an action type')
      return
    }
    const selectedType = types.find((t) => t.stage_action_types__id === editor.actionTypeId)
    const isEmail = selectedType?.stage_action_types__handler_key === 'send_email'
    if (isEmail && !editor.templateId) {
      showErrorToast('Select an email template')
      return
    }
    if (isEmail && editor.recipients.length === 0 && editor.teamRoles.length === 0) {
      showErrorToast('Add at least one To recipient (a user or a deal team role)')
      return
    }

    setSubmitting(true)
    try {
      const payload: StageActionSavePayload = {
        pipeline_config_id: editor.pipelineConfigId,
        action_type: editor.actionTypeId,
        email_design_template_id: editor.templateId,
        enabled: editor.enabled,
        recipient_user_ids: editor.recipients.map((r) => r.value),
        recipient_roles: editor.teamRoles,
        cc_recipient_user_ids: editor.ccRecipients.map((r) => r.value),
        cc_recipient_roles: editor.ccTeamRoles
      }
      const result = editor.editing
        ? await updateStageAction(editor.editing.stage_actions__id, payload, token())
        : await createStageAction(payload, token())
      if (!result.success) {
        showErrorToast(result.error || 'Failed to save stage action')
        return
      }
      toast.success(editor.editing ? 'Stage action updated' : 'Stage action created')
      setEditor(CLOSED_EDITOR)
      reload()
    } finally {
      setSubmitting(false)
    }
  }, [editor, types, reload])

  const onToggleEnabled = useCallback(
    async (row: StageActionAdminModel, enabled: boolean) => {
      setTogglingId(row.stage_actions__id)
      try {
        const result = await updateStageAction(row.stage_actions__id, { enabled }, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to update stage action')
          return
        }
        setRows((prev) =>
          prev.map((r) =>
            r.stage_actions__id === row.stage_actions__id
              ? { ...r, stage_actions__enabled: enabled }
              : r
          )
        )
      } finally {
        setTogglingId(null)
      }
    },
    []
  )

  const onDelete = useCallback(
    async (row: StageActionAdminModel) => {
      setDeletingId(row.stage_actions__id)
      try {
        const result = await deleteStageAction(row.stage_actions__id, token())
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete stage action')
          return
        }
        toast.success('Stage action deleted')
        reload()
      } finally {
        setDeletingId(null)
      }
    },
    [reload]
  )

  // Grid ---------------------------------------------------------------------

  const columns: ColumnsType<StageActionAdminModel> = [
    {
      title: 'Pipeline',
      dataIndex: 'pipelines__name',
      key: 'pipeline',
      width: 160
    },
    {
      title: 'Stage',
      dataIndex: 'pipeline_stages__name',
      key: 'stage',
      width: 140
    },
    {
      title: 'Action',
      dataIndex: 'stage_action_types__label',
      key: 'action',
      width: 140,
      render: (label: string, row) => (
        <Space size={4}>
          <ThunderboltOutlined style={{ color: '#faad14' }} />
          <span>{label}</span>
          {row.stage_action_types__timing === 'pre_transition' && (
            <Tag color='purple' style={{ margin: 0 }}>
              gate
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Email template',
      dataIndex: 'email_design_templates__name',
      key: 'template',
      width: 200,
      render: (name: string | null) => name || <Text type='secondary'>—</Text>
    },
    {
      title: 'Recipients',
      key: 'recipients',
      render: (_, row) =>
        row.recipients.length ? (
          <Space size={[4, 4]} wrap>
            {row.recipients.map((r) => {
              const ccPrefix = r.stage_action_recipients__address_mode === 'cc' ? 'Cc: ' : ''
              return r.stage_action_recipients__recipient_kind === 'team_role' ? (
                <Tooltip
                  title='Deal team role — resolved per deal at send time'
                  key={r.stage_action_recipients__id}
                >
                  <Tag color='blue' style={{ margin: 0 }}>
                    {ccPrefix}
                    {TEAM_ROLE_LABELS[r.stage_action_recipients__team_role || ''] ||
                      r.stage_action_recipients__team_role}
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip title={r.system_users__email || undefined} key={r.stage_action_recipients__id}>
                  <Tag style={{ margin: 0 }}>
                    {ccPrefix}
                    {r.system_users__name || r.system_users__email}
                  </Tag>
                </Tooltip>
              )
            })}
          </Space>
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    {
      title: 'Enabled',
      dataIndex: 'stage_actions__enabled',
      key: 'enabled',
      width: 90,
      align: 'center',
      render: (enabled: boolean, row) => (
        <Switch
          size='small'
          checked={enabled}
          loading={togglingId === row.stage_actions__id}
          onChange={(next) => onToggleEnabled(row, next)}
        />
      )
    },
    {
      title: '',
      key: 'row-actions',
      width: 90,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title='Edit'>
            <Button size='small' type='text' icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Popconfirm
            title='Delete this stage action?'
            description='The stage stops triggering this action. Past events are unaffected.'
            onConfirm={() => onDelete(row)}
            okText='Delete'
            okButtonProps={{ danger: true, loading: deletingId === row.stage_actions__id }}
          >
            <Button size='small' type='text' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ]

  const selectedType = types.find((t) => t.stage_action_types__id === editor.actionTypeId)
  const isEmailType = selectedType?.stage_action_types__handler_key === 'send_email'

  // The recipients Select must always see the CURRENT selection as options —
  // search results alone would leave pre-existing recipients labelless (AntD
  // falls back to rendering the raw id when a value has no matching option).
  const recipientOptions = useMemo(() => {
    const byValue = new Map<string, Option>()
    for (const o of [...editor.recipients, ...editor.ccRecipients, ...userOptions]) {
      if (!byValue.has(o.value)) byValue.set(o.value, o)
    }
    return [...byValue.values()]
  }, [editor.recipients, editor.ccRecipients, userOptions])

  return (
    <div className='h-full flex flex-col overflow-hidden'>
      <div className='flex items-center justify-between mb-3'>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Stage Actions
          </Title>
          <Text type='secondary'>
            Trigger actions automatically when a deal enters a pipeline stage.
          </Text>
        </div>
        <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
          Add stage action
        </Button>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table<StageActionAdminModel>
          rowKey={(r) => r.stage_actions__id}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size='small'
        />
      </div>

      <Modal
        title={editor.editing ? 'Edit stage action' : 'Add stage action'}
        open={editor.open}
        onCancel={() => setEditor(CLOSED_EDITOR)}
        onOk={submit}
        okText={editor.editing ? 'Save' : 'Create'}
        okButtonProps={{ loading: submitting }}
        width={560}
        destroyOnClose
      >
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          <div>
            <Text strong>Pipeline</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder='Select a pipeline'
              options={pipelines}
              value={editor.pipelineId}
              onChange={(pipelineId: string) => {
                setEditor((prev) => ({ ...prev, pipelineId, pipelineConfigId: null }))
                loadStages(pipelineId)
              }}
              showSearch
              optionFilterProp='label'
            />
          </div>

          <div>
            <Text strong>Stage</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder={editor.pipelineId ? 'Select a stage' : 'Select a pipeline first'}
              options={stageOptions}
              value={editor.pipelineConfigId}
              disabled={!editor.pipelineId}
              loading={stagesLoading}
              onChange={(pipelineConfigId: string) =>
                setEditor((prev) => ({ ...prev, pipelineConfigId }))
              }
              showSearch
              optionFilterProp='label'
            />
          </div>

          <div>
            <Text strong>Action</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder='Select an action type'
              options={selectableTypes.map((t) => ({
                value: t.stage_action_types__id,
                label: t.stage_action_types__label
              }))}
              value={editor.actionTypeId}
              onChange={(actionTypeId: string) => setEditor((prev) => ({ ...prev, actionTypeId }))}
            />
            {selectedType?.stage_action_types__description && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                {selectedType.stage_action_types__description}
              </Text>
            )}
          </div>

          {isEmailType && (
            <>
              <div>
                <Text strong>Email template</Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder='Select an email design template'
                  options={templates}
                  value={editor.templateId}
                  onChange={(templateId: string) => setEditor((prev) => ({ ...prev, templateId }))}
                  showSearch
                  optionFilterProp='label'
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Author templates under Email → Templates — the editor&apos;s Merge fields
                  button lists the deal fields (e.g. <Text code>{'{{deal.name}}'}</Text>)
                  substituted at send time. The email is sent from the reservoir@ mailbox.
                </Text>
              </div>

              <div>
                <Text strong>To — internal users</Text>
                <Select
                  mode='multiple'
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder='Search internal users (min 2 characters)'
                  filterOption={false}
                  onSearch={onUserSearch}
                  notFoundContent={userSearching ? <Spin size='small' /> : null}
                  options={recipientOptions}
                  value={editor.recipients.map((r) => r.value)}
                  labelInValue={false}
                  onChange={(values: string[]) => {
                    const universe = new Map(recipientOptions.map((o) => [o.value, o]))
                    setEditor((prev) => ({
                      ...prev,
                      recipients: values
                        .map((v) => universe.get(v))
                        .filter((o): o is Option => Boolean(o))
                    }))
                  }}
                />
              </div>

              <div>
                <Text strong>To — deal team roles</Text>
                <Select
                  mode='multiple'
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder='Add deal team roles (resolved per deal)'
                  options={TEAM_ROLE_OPTIONS}
                  value={editor.teamRoles}
                  onChange={(teamRoles: string[]) => setEditor((prev) => ({ ...prev, teamRoles }))}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Resolved when the email fires: whoever holds the role on that deal (Lead) or
                  its company (Sourced by, Introduced by) receives it at their contact email.
                  Roles nobody holds are skipped.
                </Text>
              </div>

              <div>
                <Text strong>Cc — internal users</Text>
                <Select
                  mode='multiple'
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder='Search internal users (min 2 characters)'
                  filterOption={false}
                  onSearch={onUserSearch}
                  notFoundContent={userSearching ? <Spin size='small' /> : null}
                  options={recipientOptions}
                  value={editor.ccRecipients.map((r) => r.value)}
                  labelInValue={false}
                  onChange={(values: string[]) => {
                    const universe = new Map(recipientOptions.map((o) => [o.value, o]))
                    setEditor((prev) => ({
                      ...prev,
                      ccRecipients: values
                        .map((v) => universe.get(v))
                        .filter((o): o is Option => Boolean(o))
                    }))
                  }}
                />
              </div>

              <div>
                <Text strong>Cc — deal team roles</Text>
                <Select
                  mode='multiple'
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder='Add deal team roles (resolved per deal)'
                  options={TEAM_ROLE_OPTIONS}
                  value={editor.ccTeamRoles}
                  onChange={(ccTeamRoles: string[]) => setEditor((prev) => ({ ...prev, ccTeamRoles }))}
                />
                <Text type='secondary' style={{ fontSize: 12 }}>
                  An address in both To and Cc is sent To only.
                </Text>
              </div>
            </>
          )}

          <div className='flex items-center gap-2'>
            <Switch
              size='small'
              checked={editor.enabled}
              onChange={(enabled) => setEditor((prev) => ({ ...prev, enabled }))}
            />
            <Text>Enabled</Text>
          </div>

          {!editor.editing && (
            <Alert
              type='info'
              showIcon
              message='Actions fire when a deal enters the stage (kanban drag, form edit, or creation directly into the stage). Processing is asynchronous — emails send within about a minute.'
            />
          )}
        </Space>
      </Modal>
    </div>
  )
}
